import { CartAddEvent } from '@theme/events';

/**
 * Build-your-own bundle: pick one duvet cover and one sheet set, optionally add a duvet insert, review, and add
 * all lines to the cart together.
 *
 * Data source: /collections/<handle>/products.json (public, no API keys). Duvets and sheets are filtered by product
 * type, so no collections need to be created. Each product is expected to carry a Color option and optionally a
 * Size / "Bedding size" option. The "range" (Muse, Adorn) is derived from the product title before the first "|".
 * The duvet insert is found by product tag (default "duvet-insert") or product type, and only needs a Size option.
 * If no insert product is found, or extras are switched off, the Extras step is skipped.
 *
 * Cart payload: every line gets a visible "Bundle" property (shown in the cart) plus hidden _bundle_* properties
 * that a discount rule, Shopflo, or a later cart-grouping change can read.
 */

const LABELS = { duvet: 'Duvet cover', sheet: 'Sheet set', extras: 'Extras', review: 'Review' };

/** @param {unknown} value */
const esc = (value) =>
  String(value ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c] ?? c);

/** @param {string} value */
const escapeRegExp = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/** @template T @param {T[]} list @returns {T[]} */
const uniq = (list) => [...new Set(list)];

/** @param {string} src @param {number} width */
const resize = (src, width) => {
  if (!src) return '';
  try {
    const url = new URL(src, window.location.origin);
    url.searchParams.set('width', String(width));
    return url.toString();
  } catch (_) {
    return src;
  }
};

/**
 * @typedef {object} Item
 * @property {'duvet'|'sheet'|'insert'} cat
 * @property {string} range
 * @property {string} size
 * @property {string} colour
 * @property {string} title
 * @property {string} url
 * @property {number} variantId
 * @property {number} price
 * @property {boolean} available
 * @property {string} image
 */

class BundleBuilder extends HTMLElement {
  /** @type {AbortController} */
  #abort = new AbortController();

  /** @type {{ duvet: Item[], sheet: Item[] }} */
  #catalog = { duvet: [], sheet: [] };

  /**
   * The optional duvet insert: one product, shown as a showcase card in the Extras step.
   * @type {{ title: string, url: string, images: string[], features: string[], items: Item[] } | null}
   */
  #insert = null;

  #state = {
    /** @type {'loading'|'ready'|'error'} */
    status: 'loading',
    step: 0,
    adding: false,
    added: false,
    error: '',
    choice: {
      duvet: { range: '', size: '', colour: '' },
      sheet: { range: '', size: '', colour: '' },
    },
    insert: { size: '', added: false, image: 0 },
  };

  connectedCallback() {
    this.#abort = new AbortController();
    this.addEventListener('click', this.#onClick, { signal: this.#abort.signal });
    this.#render();
    this.#load();
  }

  disconnectedCallback() {
    this.#abort.abort();
  }

  get #config() {
    return {
      duvetType: this.dataset.duvetType || 'Duvet Cover',
      sheetType: this.dataset.sheetType || 'Sheet Set',
      collection: this.dataset.collection || 'all',
      discount: Math.max(0, Number(this.dataset.discount) || 0),
      discountNote: this.dataset.discountNote || '',
      currency: this.dataset.currency || 'INR',
      locale: this.dataset.locale || undefined,
      cartUrl: this.dataset.cartUrl || '/cart',
      extras: this.dataset.extras !== 'false',
      insertTag: (this.dataset.insertTag || 'duvet-insert').trim().toLowerCase(),
      insertType: (this.dataset.insertType || '').trim().toLowerCase(),
      extrasHeading: this.dataset.extrasHeading || 'Complete the bed with a duvet insert',
      extrasText: this.dataset.extrasText || 'Optional. It sits inside your duvet cover.',
    };
  }

  /** Steps in order. The Extras step only exists when an insert product was found. */
  get #stepKeys() {
    return this.#insert ? ['duvet', 'sheet', 'extras', 'review'] : ['duvet', 'sheet', 'review'];
  }

  /** @param {number} amount */
  #money(amount) {
    try {
      return new Intl.NumberFormat(this.#config.locale, {
        style: 'currency',
        currency: this.#config.currency,
        maximumFractionDigits: 0,
      }).format(amount);
    } catch (_) {
      return String(Math.round(amount));
    }
  }

  async #load() {
    const { collection, duvetType, sheetType } = this.#config;
    try {
      const response = await fetch(`/collections/${encodeURIComponent(collection)}/products.json?limit=250`, {
        signal: this.#abort.signal,
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) throw new Error(`products.json ${response.status}`);
      const { products = [] } = await response.json();
      this.#catalog.duvet = this.#parse(products, duvetType, 'duvet');
      this.#catalog.sheet = this.#parse(products, sheetType, 'sheet');
      if (!this.#catalog.duvet.length || !this.#catalog.sheet.length) throw new Error('No matching products');
      this.#normalise('duvet');
      this.#normalise('sheet');
      // The insert is optional: a problem finding it must never break the main builder.
      try {
        this.#insert = this.#config.extras ? this.#parseInsert(products) : null;
      } catch (error) {
        console.warn('[bundle-builder] extras skipped', error);
        this.#insert = null;
      }
      this.#state.status = 'ready';
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      console.error('[bundle-builder]', error);
      this.#state.status = 'error';
    }
    this.#render();
  }

  /**
   * @param {any[]} products
   * @param {string} type
   * @param {'duvet'|'sheet'} cat
   * @returns {Item[]}
   */
  #parse(products, type, cat) {
    const wanted = type.trim().toLowerCase();
    /** @type {Item[]} */
    const items = [];
    for (const product of products) {
      if (String(product.product_type || '').trim().toLowerCase() !== wanted) continue;
      const options = product.options || [];
      const sizeIndex = options.findIndex((/** @type {any} */ o) => /size/i.test(o.name));
      const colourIndex = options.findIndex((/** @type {any} */ o) => /colou?r/i.test(o.name));
      const range =
        String(product.title).split('|')[0].replace(new RegExp(escapeRegExp(type), 'i'), '').trim() || product.title;

      for (const variant of product.variants || []) {
        const values = [variant.option1, variant.option2, variant.option3];
        const image = variant.featured_image?.src || product.images?.[0]?.src || product.image?.src || '';
        const item = {
          cat,
          range,
          size: sizeIndex >= 0 ? String(values[sizeIndex] ?? '') : '',
          colour: colourIndex >= 0 ? String(values[colourIndex] ?? '') : '',
          title: product.title,
          url: `/products/${product.handle}`,
          variantId: variant.id,
          price: parseFloat(variant.price),
          available: Boolean(variant.available),
          image,
        };
        // Same range, size and colour can appear on more than one product; keep the first, prefer one in stock.
        const existing = items.find(
          (i) => i.range === item.range && i.size === item.size && i.colour === item.colour
        );
        if (!existing) items.push(item);
        else if (!existing.available && item.available) Object.assign(existing, item);
      }
    }
    return items;
  }

  /**
   * Finds the duvet insert product (by tag, or by product type when one is set) and reads its variants, images and
   * the bullet points from its description, so the showcase always matches the product page.
   * @param {any[]} products
   */
  #parseInsert(products) {
    const { insertTag, insertType } = this.#config;
    const product = products.find((/** @type {any} */ p) => {
      const tags = (Array.isArray(p.tags) ? p.tags : String(p.tags || '').split(',')).map((/** @type {string} */ t) =>
        String(t).trim().toLowerCase()
      );
      const type = String(p.product_type || '').trim().toLowerCase();
      return (insertTag && tags.includes(insertTag)) || (insertType && type === insertType);
    });
    if (!product) return null;

    const options = product.options || [];
    const sizeIndex = options.findIndex((/** @type {any} */ o) => /size/i.test(o.name));
    const images = uniq(
      /** @type {string[]} */ ((product.images || []).map((/** @type {any} */ i) => i.src).filter(Boolean))
    ).slice(0, 4);

    /** @type {Item[]} */
    const items = (product.variants || []).map((/** @type {any} */ variant) => {
      const values = [variant.option1, variant.option2, variant.option3];
      return {
        cat: /** @type {const} */ ('insert'),
        range: product.title,
        size: sizeIndex >= 0 ? String(values[sizeIndex] ?? '') : '',
        colour: '',
        title: product.title,
        url: `/products/${product.handle}`,
        variantId: variant.id,
        price: parseFloat(variant.price),
        available: Boolean(variant.available),
        image: variant.featured_image?.src || images[0] || '',
      };
    });
    if (!items.length) return null;

    /** @type {string[]} */
    let features = [];
    try {
      const doc = new DOMParser().parseFromString(String(product.body_html || ''), 'text/html');
      features = [...doc.querySelectorAll('li')]
        .map((li) => (li.textContent || '').replace(/\s+/g, ' ').trim())
        .filter((text) => text && !/^content\s*:/i.test(text))
        .slice(0, 6);
    } catch (_) {
      features = [];
    }

    const state = this.#state.insert;
    const sizes = uniq(items.map((i) => i.size));
    if (sizes.length === 1) state.size = sizes[0];
    return { title: String(product.title), url: `/products/${product.handle}`, images, features, items };
  }

  /** @returns {Item | null} The insert variant that is currently chosen, whether or not it was added. */
  #insertItem() {
    if (!this.#insert) return null;
    const { size } = this.#state.insert;
    return this.#insert.items.find((i) => i.size === size) ?? null;
  }

  /** @returns {Item | null} The insert that will go in the bundle (chosen and added). */
  #insertInBundle() {
    return this.#insert && this.#state.insert.added ? this.#insertItem() : null;
  }

  /**
   * Pre-selects the insert size that matches the chosen duvet cover (a King duvet gets a King insert).
   * Only runs when the shopper has not chosen an insert size yet.
   */
  #suggestInsertSize() {
    if (!this.#insert) return;
    const state = this.#state.insert;
    if (state.size) return;
    const duvetSize = this.#selected('duvet')?.size.toLowerCase();
    const match = this.#insert.items.find((i) => i.available && i.size.toLowerCase() === duvetSize);
    if (match) state.size = match.size;
  }

  /**
   * Keeps a category's choice valid and auto-selects options that have only one value.
   * @param {'duvet'|'sheet'} cat
   */
  #normalise(cat) {
    const items = this.#catalog[cat];
    const choice = this.#state.choice[cat];

    const ranges = uniq(items.map((i) => i.range));
    if (ranges.length === 1) choice.range = ranges[0];
    if (!ranges.includes(choice.range)) choice.range = '';

    const inRange = items.filter((i) => i.range === choice.range);
    const sizes = uniq(inRange.map((i) => i.size));
    if (sizes.length === 1 && choice.range) choice.size = sizes[0];
    if (!sizes.includes(choice.size)) choice.size = '';

    const colours = inRange.filter((i) => i.size === choice.size).map((i) => i.colour);
    if (!choice.range || (sizes.length > 1 && !choice.size) || !colours.includes(choice.colour)) choice.colour = '';
  }

  /** @param {'duvet'|'sheet'} cat @returns {Item | null} */
  #selected(cat) {
    const { range, size, colour } = this.#state.choice[cat];
    if (!range || !colour) return null;
    return this.#catalog[cat].find((i) => i.range === range && i.size === size && i.colour === colour) ?? null;
  }

  /** @param {'duvet'|'sheet'} cat */
  #groups(cat) {
    const items = this.#catalog[cat];
    const choice = this.#state.choice[cat];
    const ranges = uniq(items.map((i) => i.range));
    const inRange = items.filter((i) => i.range === choice.range);
    const sizes = uniq(inRange.map((i) => i.size)).filter(Boolean);
    const needsSize = sizes.length > 1;
    const colourItems =
      !choice.range || (needsSize && !choice.size) ? [] : inRange.filter((i) => i.size === choice.size);
    return { ranges, sizes, needsSize, colourItems, inRange };
  }

  /** @param {'duvet'|'sheet'} cat @param {string} size */
  #sizeAvailable(cat, size) {
    const { range } = this.#state.choice[cat];
    return this.#catalog[cat].some((i) => i.range === range && i.size === size && i.available);
  }

  /** @param {Event} event */
  #onClick = (event) => {
    const target = event.target instanceof Element ? event.target.closest('[data-action]') : null;
    if (!(target instanceof HTMLElement) || !this.contains(target)) return;
    if (target.hasAttribute('disabled') || target.getAttribute('aria-disabled') === 'true') return;

    const { action, value = '' } = target.dataset;
    const cat = /** @type {'duvet'|'sheet'|undefined} */ (this.#stepKeys[this.#state.step]);
    const isCategoryStep = cat === 'duvet' || cat === 'sheet';
    const state = this.#state;
    let announce = '';

    if (isCategoryStep && (action === 'range' || action === 'size' || action === 'colour')) {
      state.choice[cat][action] = value;
      this.#normalise(cat);
      state.added = false;
      state.error = '';
      announce = `${value} selected`;
    } else if (action === 'insert-size') {
      state.insert.size = value;
      state.added = false;
      state.error = '';
      announce = `${value} selected`;
    } else if (action === 'insert-add') {
      // Needs a size chosen and in stock (single-size inserts are pre-selected).
      if (!this.#insertItem()?.available) return;
      state.insert.added = true;
      state.added = false;
      state.error = '';
      announce = 'Duvet insert added to your bundle';
    } else if (action === 'insert-remove') {
      state.insert.added = false;
      state.added = false;
      state.error = '';
      announce = 'Duvet insert removed from your bundle';
    } else if (action === 'insert-image') {
      state.insert.image = Number(value) || 0;
    } else if (action === 'next') {
      if (isCategoryStep && !this.#selected(cat)) return;
      this.#goTo(state.step + 1);
      return;
    } else if (action === 'back') {
      this.#goTo(state.step - 1);
      return;
    } else if (action === 'goto') {
      this.#goTo(Number(value));
      return;
    } else if (action === 'add') {
      this.#add();
      return;
    } else {
      return;
    }

    this.#render(announce);
  };

  /** @param {number} step */
  #goTo(step) {
    const state = this.#state;
    const keys = this.#stepKeys;
    const next = Math.min(Math.max(step, 0), keys.length - 1);
    if (next > this.#unlocked()) return;
    state.step = next;
    state.error = '';
    if (keys[next] === 'extras') this.#suggestInsertSize();
    this.#render(`Step ${next + 1} of ${keys.length}`, true);
  }

  /**
   * Highest step the shopper may open: the sheet step needs a duvet cover, and everything after it (the optional
   * Extras step, then Review) needs a sheet set.
   */
  #unlocked() {
    if (!this.#selected('duvet')) return 0;
    if (!this.#selected('sheet')) return 1;
    return this.#stepKeys.length - 1;
  }

  async #add() {
    const duvet = this.#selected('duvet');
    const sheet = this.#selected('sheet');
    const state = this.#state;
    if (!duvet || !sheet || state.adding) return;

    state.adding = true;
    state.error = '';
    this.#render();

    const insert = this.#insertInBundle();
    const { discount } = this.#config;
    const properties = {
      Bundle: 'Build your own',
      _bundle_id: `byo-${Date.now().toString(36)}`,
      _bundle_type: insert ? 'duvet-sheets-insert' : 'duvet-and-sheets',
      _bundle_discount_pct: String(discount),
    };
    /** @type {{ id: number, quantity: number, properties: Record<string, string> }[]} */
    const items = [
      { id: duvet.variantId, quantity: 1, properties: { ...properties, _bundle_role: 'duvet' } },
      { id: sheet.variantId, quantity: 1, properties: { ...properties, _bundle_role: 'sheet' } },
    ];
    if (insert) items.push({ id: insert.variantId, quantity: 1, properties: { ...properties, _bundle_role: 'insert' } });

    const sectionIds = uniq(
      [...document.querySelectorAll('cart-items-component')]
        .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId || '' : ''))
        .filter(Boolean)
    );

    try {
      const routes = /** @type {any} */ (window).Theme?.routes ?? {};
      const response = await fetch(routes.cart_add_url || '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections: sectionIds.join(',') }),
      });
      const data = await response.json();
      if (!response.ok || data.status) {
        throw new Error(data.description || data.message || 'Could not add the bundle to your bag.');
      }

      const cartResponse = await fetch(`${routes.cart_url || '/cart'}.js`, { headers: { Accept: 'application/json' } });
      const cart = await cartResponse.json();

      state.added = true;
      this.dispatchEvent(
        new CartAddEvent(cart, 'bundle-builder', {
          source: 'bundle-builder',
          itemCount: cart.item_count,
          sections: data.sections,
        })
      );
    } catch (error) {
      console.error('[bundle-builder]', error);
      state.error = error instanceof Error ? error.message : 'Something went wrong. Please try again.';
    } finally {
      state.adding = false;
      this.#render(state.added ? 'Bundle added to your bag' : state.error);
    }
  }

  /**
   * @param {string} [announce] Text for the screen reader live region
   * @param {boolean} [focusHeading] Move focus to the step heading (used on step change)
   */
  #render(announce = '', focusHeading = false) {
    const steps = this.querySelector('[data-bb-steps]');
    const panel = this.querySelector('[data-bb-panel]');
    const preview = this.querySelector('[data-bb-preview]');
    const live = this.querySelector('[data-bb-live]');
    if (!steps || !panel || !preview) return;

    const active = document.activeElement;
    const focusKey = active instanceof HTMLElement && this.contains(active) ? active.dataset.key : '';

    // Only touch the DOM when the markup changed, so preview images do not reload and flash on every click.
    this.#set(steps, 'steps', this.#stepsHtml());
    this.#set(preview, 'preview', this.#previewHtml());
    this.#set(panel, 'panel', this.#panelHtml());
    if (live && announce) live.textContent = announce;

    if (focusHeading) {
      const heading = panel.querySelector('[data-bb-heading]');
      if (heading instanceof HTMLElement) heading.focus({ preventScroll: false });
    } else if (focusKey) {
      const again = [...this.querySelectorAll('[data-key]')].find(
        (el) => el instanceof HTMLElement && el.dataset.key === focusKey
      );
      if (again instanceof HTMLElement) again.focus({ preventScroll: true });
    }
  }

  /** @type {Record<string, string>} */
  #html = {};

  /** @param {Element} el @param {string} key @param {string} html */
  #set(el, key, html) {
    if (this.#html[key] === html) return;
    this.#html[key] = html;
    el.innerHTML = html;
  }

  #stepsHtml() {
    const { step } = this.#state;
    const unlocked = this.#unlocked();
    const labels = this.#stepKeys.map((key) => LABELS[/** @type {keyof typeof LABELS} */ (key)]);
    return labels
      .map((label, index) => {
        const done = index < unlocked && index !== step;
        const reachable = index <= unlocked && this.#state.status === 'ready';
        return `<li class="bb__step${index === step ? ' is-current' : ''}${done ? ' is-done' : ''}">
          <button type="button" class="bb__step-btn" data-action="goto" data-value="${index}" data-key="step-${index}"
            ${reachable ? '' : 'disabled'} ${index === step ? 'aria-current="step"' : ''}>
            <span class="bb__step-num" aria-hidden="true">${index + 1}</span>
            <span class="bb__step-label">${esc(label)}</span>
          </button>
        </li>`;
      })
      .join('');
  }

  #previewHtml() {
    const frame = (/** @type {'duvet'|'sheet'} */ cat, /** @type {string} */ label) => {
      const item = this.#selected(cat);
      const image = item?.image ? `<img src="${esc(resize(item.image, 640))}" alt="" loading="lazy" width="640" height="800">` : '';
      const caption = item ? `${esc(item.colour)}${item.size ? `, ${esc(item.size)}` : ''}` : 'Not chosen yet';
      return `<figure class="bb__frame${item ? ' has-image' : ''}">
        <div class="bb__frame-media">${image}<span class="bb__frame-empty">${esc(label)}</span></div>
        <figcaption><strong>${esc(label)}</strong><span>${caption}</span></figcaption>
      </figure>`;
    };
    let html = frame('duvet', 'Duvet cover') + frame('sheet', 'Sheet set');

    // The insert frame appears once the shopper reaches the Extras step (or has added an insert).
    const extrasIndex = this.#stepKeys.indexOf('extras');
    if (extrasIndex >= 0 && (this.#state.step >= extrasIndex || this.#insertInBundle())) {
      const item = this.#insertInBundle();
      const image = item?.image ? `<img src="${esc(resize(item.image, 640))}" alt="" loading="lazy" width="640" height="800">` : '';
      html += `<figure class="bb__frame${item ? ' has-image' : ''}">
        <div class="bb__frame-media">${image}<span class="bb__frame-empty">Duvet insert (optional)</span></div>
        <figcaption><strong>Duvet insert</strong><span>${item ? esc(item.size || 'Added') : 'Not added'}</span></figcaption>
      </figure>`;
    }
    return html;
  }

  #panelHtml() {
    const { status, step } = this.#state;
    if (status === 'loading') return '<p class="bb__note">Loading colours…</p>';
    if (status === 'error') {
      return `<p class="bb__note">We could not load the bundle builder. <a href="/collections/all">Shop all products</a>.</p>`;
    }
    const key = this.#stepKeys[step];
    if (key === 'review') return this.#reviewHtml();
    if (key === 'extras') return this.#extrasHtml();
    return this.#chooseHtml(/** @type {'duvet'|'sheet'} */ (key));
  }

  #extrasHtml() {
    const insert = this.#insert;
    if (!insert) return this.#reviewHtml();
    const { extrasHeading, extrasText } = this.#config;
    const state = this.#state.insert;
    const sizes = uniq(insert.items.map((i) => i.size)).filter(Boolean);
    const chosen = this.#insertItem();
    const added = Boolean(this.#insertInBundle());

    const shown = insert.images[state.image] || chosen?.image || insert.images[0] || '';
    const gallery =
      insert.images.length > 1
        ? `<div class="bb__thumbs">${insert.images
            .map(
              (src, index) => `<button type="button" class="bb__thumb" data-action="insert-image" data-value="${index}"
                data-key="thumb-${index}" aria-pressed="${index === state.image}" aria-label="Show image ${index + 1}">
                <img src="${esc(resize(src, 120))}" alt="" loading="lazy" width="120" height="150">
              </button>`
            )
            .join('')}</div>`
        : '';

    const sizeGroup =
      sizes.length > 1
        ? `<fieldset class="bb__group"><legend>Size</legend><div class="bb__chips">
            ${sizes
              .map((s) => {
                const ok = insert.items.some((i) => i.size === s && i.available);
                return `<button type="button" class="bb__chip" data-action="insert-size" data-value="${esc(s)}" data-key="insize-${esc(s)}"
                  aria-pressed="${s === state.size}" ${ok ? '' : 'disabled'}>${esc(s)}${ok ? '' : ' <span class="bb__chip-note">Sold out</span>'}</button>`;
              })
              .join('')}
          </div></fieldset>`
        : '';

    const action = added
      ? `<button type="button" class="button button-secondary bb__btn" data-action="insert-remove" data-key="insert-remove">Remove duvet insert</button>`
      : `<button type="button" class="button bb__btn" data-action="insert-add" data-key="insert-add" ${chosen?.available ? '' : 'disabled'}>
          Add duvet insert${chosen ? ` · ${this.#money(chosen.price)}` : ''}
        </button>`;

    return `<h3 class="bb__title" tabindex="-1" data-bb-heading>${esc(extrasHeading)}</h3>
      <div class="bb__extra${added ? ' is-added' : ''}">
        <div class="bb__extra-media">
          ${shown ? `<img src="${esc(resize(shown, 720))}" alt="${esc(insert.title)}" loading="lazy" width="720" height="900">` : ''}
        </div>
        ${gallery}
        <div class="bb__extra-body">
          <p class="bb__extra-name"><strong>${esc(insert.title)}</strong>
            <span class="bb__price">${chosen ? this.#money(chosen.price) : `From ${this.#money(Math.min(...insert.items.map((i) => i.price)))}`}</span></p>
          ${extrasText ? `<p class="bb__hint">${esc(extrasText)}</p>` : ''}
          ${insert.features.length ? `<ul class="bb__features">${insert.features.map((f) => `<li>${esc(f)}</li>`).join('')}</ul>` : ''}
        </div>
      </div>
      ${sizeGroup}
      ${added ? '<p class="bb__extra-status">Added to your bundle.</p>' : ''}
      <div class="bb__extra-action">${action}</div>
      <p class="bb__hint"><a href="${esc(insert.url)}" target="_blank" rel="noopener">View full details</a></p>
      <div class="bb__nav">
        <button type="button" class="button button-secondary bb__btn" data-action="back" data-key="back">Back</button>
        <button type="button" class="button bb__btn" data-action="next" data-key="next">
          ${added ? 'Next: review' : 'Skip: review'}
        </button>
      </div>`;
  }

  /** @param {'duvet'|'sheet'} cat */
  #chooseHtml(cat) {
    const choice = this.#state.choice[cat];
    const { ranges, sizes, needsSize, colourItems } = this.#groups(cat);
    const selected = this.#selected(cat);
    const title = cat === 'duvet' ? 'Choose your duvet cover' : 'Choose your sheet set';

    const chip = (/** @type {string} */ action, /** @type {string} */ value, /** @type {boolean} */ pressed, /** @type {boolean} */ disabled = false) =>
      `<button type="button" class="bb__chip" data-action="${action}" data-value="${esc(value)}" data-key="${action}-${esc(value)}"
        aria-pressed="${pressed}" ${disabled ? 'disabled' : ''}>${esc(value)}${disabled ? ' <span class="bb__chip-note">Sold out</span>' : ''}</button>`;

    let html = `<h3 class="bb__title" tabindex="-1" data-bb-heading>${esc(title)}</h3>`;

    if (ranges.length > 1) {
      html += `<fieldset class="bb__group"><legend>Range</legend><div class="bb__chips">
        ${ranges.map((r) => chip('range', r, r === choice.range)).join('')}
      </div></fieldset>`;
    }

    if (choice.range && needsSize) {
      html += `<fieldset class="bb__group"><legend>Size</legend><div class="bb__chips">
        ${sizes.map((s) => chip('size', s, s === choice.size, !this.#sizeAvailable(cat, s))).join('')}
      </div></fieldset>`;
    }

    html += `<fieldset class="bb__group"><legend>Colour${selected ? `: <span class="bb__legend-value">${esc(selected.colour)}</span>` : ''}</legend>`;
    if (!choice.range) {
      html += '<p class="bb__hint">Choose a range to see colours.</p>';
    } else if (needsSize && !choice.size) {
      html += '<p class="bb__hint">Choose a size to see colours.</p>';
    } else {
      html += `<div class="bb__tiles">${colourItems
        .map(
          (item) => `<button type="button" class="bb__tile" data-action="colour" data-value="${esc(item.colour)}"
            data-key="colour-${esc(item.colour)}" aria-pressed="${item.colour === choice.colour}" ${item.available ? '' : 'disabled'}>
            <span class="bb__tile-media">${item.image ? `<img src="${esc(resize(item.image, 240))}" alt="" loading="lazy" width="240" height="300">` : ''}</span>
            <span class="bb__tile-name">${esc(item.colour)}</span>
            ${item.available ? '' : '<span class="bb__tile-note">Sold out</span>'}
          </button>`
        )
        .join('')}</div>`;
    }
    html += '</fieldset>';

    if (selected) {
      html += `<p class="bb__selected">${esc(selected.colour)}${selected.size ? `, ${esc(selected.size)}` : ''}
        <span class="bb__price">${this.#money(selected.price)}</span></p>`;
    }

    html += `<div class="bb__nav">
      ${this.#state.step > 0 ? '<button type="button" class="button button-secondary bb__btn" data-action="back" data-key="back">Back</button>' : '<span></span>'}
      <button type="button" class="button bb__btn" data-action="next" data-key="next" ${selected ? '' : 'disabled'}>
        ${cat === 'duvet' ? 'Next: sheet set' : this.#insert ? 'Next: extras' : 'Next: review'}
      </button>
    </div>`;
    return html;
  }

  #reviewHtml() {
    const duvet = this.#selected('duvet');
    const sheet = this.#selected('sheet');
    const { discount, discountNote, cartUrl } = this.#config;
    const state = this.#state;
    if (!duvet || !sheet) return '<p class="bb__note">Choose a duvet cover and a sheet set to review your bundle.</p>';

    // The bundle discount applies to the duvet cover and sheet set. The insert is an extra at its normal price.
    const insert = this.#insertInBundle();
    const bundleSubtotal = duvet.price + sheet.price;
    const subtotal = bundleSubtotal + (insert ? insert.price : 0);
    const saving = discount > 0 ? Math.round((bundleSubtotal * discount) / 100) : 0;
    const total = subtotal - saving;
    const extrasIndex = this.#stepKeys.indexOf('extras');

    const line = (
      /** @type {Item} */ item,
      /** @type {number} */ index,
      /** @type {string} */ label,
      /** @type {string} */ extra = ''
    ) => `
      <li class="bb__line">
        <span class="bb__line-media">${item.image ? `<img src="${esc(resize(item.image, 160))}" alt="" loading="lazy" width="160" height="200">` : ''}</span>
        <span class="bb__line-body">
          <strong>${esc(label)}</strong>
          <span>${esc([item.colour, item.size].filter(Boolean).join(', '))}</span>
          <span class="bb__line-actions">
            <button type="button" class="bb__link" data-action="goto" data-value="${index}" data-key="edit-${index}">Change</button>
            ${extra}
          </span>
        </span>
        <span class="bb__line-price">${this.#money(item.price)}</span>
      </li>`;

    const insertLine = insert
      ? line(
          insert,
          extrasIndex,
          'Duvet insert',
          `<button type="button" class="bb__link" data-action="insert-remove" data-key="remove-insert">Remove</button>`
        )
      : '';
    const addInsertPrompt =
      !insert && this.#insert
        ? `<p class="bb__hint">Want a duvet insert to go with it? <button type="button" class="bb__link bb__link--inline" data-action="goto" data-value="${extrasIndex}" data-key="add-insert">Add one</button></p>`
        : '';

    return `<h3 class="bb__title" tabindex="-1" data-bb-heading>Review your bundle</h3>
      <ul class="bb__lines">${line(duvet, 0, 'Duvet cover')}${line(sheet, 1, 'Sheet set')}${insertLine}</ul>
      ${addInsertPrompt}
      <dl class="bb__totals">
        ${saving ? `<div><dt>Subtotal</dt><dd>${this.#money(subtotal)}</dd></div>
        <div><dt>Bundle saving (${discount}%)</dt><dd>-${this.#money(saving)}</dd></div>` : ''}
        <div class="bb__total"><dt>Total</dt><dd>${this.#money(total)}</dd></div>
      </dl>
      ${saving && discountNote ? `<p class="bb__hint">${esc(discountNote)}</p>` : ''}
      ${state.error ? `<p class="bb__error" role="alert">${esc(state.error)}</p>` : ''}
      ${state.added ? `<p class="bb__success">Added to your bag. <a href="${esc(cartUrl)}">View bag</a></p>` : ''}
      <div class="bb__nav">
        <button type="button" class="button button-secondary bb__btn" data-action="back" data-key="back">Back</button>
        <button type="button" class="button bb__btn" data-action="add" data-key="add" ${state.adding ? 'disabled aria-busy="true"' : ''}>
          ${state.adding ? 'Adding…' : state.added ? 'Add another bundle' : 'Add bundle to bag'}
        </button>
      </div>`;
  }
}

if (!customElements.get('bundle-builder')) {
  customElements.define('bundle-builder', BundleBuilder);
}